/**
 * Stripe Integration Service
 *
 * Handles Stripe Connect for practitioner payments.
 * Uses payment-service for dual-mode support (localStorage/Stripe).
 */

import paymentService from './payment-service';

/**
 * Connect Stripe Account
 * Opens Stripe Connect onboarding flow
 * @returns {Promise<{success: boolean, message?: string, account?: object}>}
 */
export const connectStripe = async () => {
  try {
    console.log('🔷 stripe-service: connectStripe called');

    // Get practitioner info from localStorage
    const setupData = localStorage.getItem('practitionerSetup');
    let email = 'practitioner@example.com';
    let businessName = 'My Practice';
    let userId = 'user_' + Date.now();

    if (setupData) {
      const parsed = JSON.parse(setupData);
      email = parsed.wizardData?.profile?.email || email;
      businessName = parsed.wizardData?.profile?.displayName || businessName;
      userId = parsed.userId || userId;
    }

    console.log('🔷 stripe-service: User data:', { userId, email, businessName });

    // Create connect account
    console.log('🔷 stripe-service: Calling paymentService.createConnectAccount');
    const result = await paymentService.createConnectAccount(userId, email, businessName);
    console.log('🔷 stripe-service: createConnectAccount result:', result);

    if (result.success) {
      if (result.redirecting) {
        // Real Stripe mode - will redirect to Stripe
        return {
          success: true,
          message: 'Redirecting to Stripe...'
        };
      }

      // localStorage mode - simulate success
      const mockAccount = {
        email: email,
        bankAccount: {
          last4: '6789',
          bankName: 'Mock Bank'
        },
        payoutSchedule: 'Daily',
        accountId: result.accountId
      };

      // Store in legacy format for compatibility
      localStorage.setItem('stripeConnected', 'true');
      localStorage.setItem('stripeAccount', JSON.stringify(mockAccount));

      return {
        success: true,
        account: mockAccount,
        message: 'Stripe connected successfully!'
      };
    }

    return {
      success: false,
      message: result.error || 'Failed to connect Stripe'
    };

  } catch (error) {
    console.error('Connect Stripe error:', error);
    return {
      success: false,
      message: error.message || 'An error occurred'
    };
  }
};

/**
 * Disconnect Stripe Account
 * @returns {Promise<{success: boolean}>}
 */
export const disconnectStripe = async () => {
  try {
    await paymentService.disconnectStripeConnect();

    // Clear legacy storage
    localStorage.removeItem('stripeConnected');
    localStorage.removeItem('stripeAccount');

    return { success: true };
  } catch (error) {
    console.error('Disconnect Stripe error:', error);
    return { success: false };
  }
};

/**
 * Get Stripe Connection Status
 * @returns {Promise<{connected: boolean, account?: object}>}
 */
export const getStripeConnectionStatus = async () => {
  try {
    // Check new format first
    const connectData = paymentService.getStoredConnectStatus();

    if (connectData && connectData.connected) {
      return {
        connected: true,
        account: {
          email: connectData.email,
          accountId: connectData.accountId,
          bankAccount: {
            last4: '6789', // Mock for now
            bankName: 'Mock Bank'
          },
          payoutSchedule: 'Daily'
        }
      };
    }

    // Check legacy format
    const legacyConnected = localStorage.getItem('stripeConnected') === 'true';
    const legacyAccount = legacyConnected
      ? JSON.parse(localStorage.getItem('stripeAccount') || '{}')
      : null;

    return {
      connected: legacyConnected,
      account: legacyAccount
    };

  } catch (error) {
    console.error('Get connection status error:', error);
    return {
      connected: false,
      account: null
    };
  }
};

/**
 * Reconnect/Update Stripe Account
 * Opens Stripe onboarding again for updates
 * @param {string} accountId - Stripe account ID
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const reconnectStripe = async (accountId) => {
  try {
    const result = await paymentService.reconnectAccount(accountId);

    if (result.success) {
      if (result.redirecting) {
        return {
          success: true,
          message: 'Redirecting to Stripe...'
        };
      }

      return {
        success: true,
        message: 'Reconnection successful!'
      };
    }

    return {
      success: false,
      message: result.error || 'Failed to reconnect'
    };

  } catch (error) {
    console.error('Reconnect Stripe error:', error);
    return {
      success: false,
      message: error.message || 'An error occurred'
    };
  }
};

// Legacy exports for compatibility
export const checkStripeConnection = async (userId) => {
  const status = await getStripeConnectionStatus();
  return {
    connected: status.connected,
    accountId: status.account?.accountId || null,
    accountStatus: status.connected ? 'active' : null,
  };
};

export const getAccountStatus = async (userId) => {
  const status = await getStripeConnectionStatus();
  return {
    connected: status.connected,
    payoutsEnabled: status.connected,
    chargesEnabled: status.connected,
  };
};

const stripeService = {
  connectStripe,
  disconnectStripe,
  reconnectStripe,
  getStripeConnectionStatus,
  checkStripeConnection,
  getAccountStatus,
};

export default stripeService;
